import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarErrorConfig } from 'src/app/_utils/_material-import/snackbar-config';
import { ValidationProblem } from 'src/app/_utils/_validators/_models/validation-problem';
import { DialogRedireccionComponent } from 'src/app/_shared/_dialogs/dialog-redireccion/dialog-redireccion.component';
import { BaseService, DOMAIN_ENVIRONMENT } from 'src/app/_services/base.service';
import { environment } from 'src/environments/environment';
import { isPlatformBrowser, registerLocaleData } from '@angular/common';
import es from '@angular/common/locales/es';

@Injectable({
  providedIn: 'root'
})
export class PlatformService extends BaseService
{
  constructor(
    @Inject(PLATFORM_ID) private _platformBrowserId,
    _http: HttpClient,
    private _router: Router,
    private _dialog: MatDialog,
    private _matSnackBar: MatSnackBar,
  )
  {
    super(_http, "Platform", "v1");
  }

  // Establece el dominio en el environment
  setPlatformEnvironment(origin: string): void
  {
    const stagingVenture = "devventure.segofinance.com";
    const stagingRealEstate = "devrealestate.segofinance.com";
    const stagingFactoring = "gray-sky-0f2907703-segofactoring.westeurope.5.azurestaticapps.net";
    const stagingFunds = "devfunds.segofinance.com";
    const stagingCreative = "devcreative.segofinance.com";

    if (origin.includes("venture.segofinance.com") || origin.includes(stagingVenture) || origin.includes("localhost:4201"))
    {
      environment.linea = "venture";
      this._domainEnvironment = DOMAIN_ENVIRONMENT.VENTURE;
    }
    else if (origin.includes("realestate.segofinance.com") || origin.includes(stagingRealEstate) || origin.includes("localhost:4202"))
    {
      environment.linea = "realestate";
      this._domainEnvironment = DOMAIN_ENVIRONMENT.REALESTATE;
    }
    else if (origin.includes("factoring.segofinance.com") || origin.includes(stagingFactoring) || origin.includes("localhost:4203"))
    {
      environment.linea = "factoring";
      this._domainEnvironment = DOMAIN_ENVIRONMENT.FACTORING;
    }
    else if (origin.includes("funds.segofinance.com") || origin.includes(stagingFunds) || origin.includes("localhost:4204"))
    {
      environment.linea = "funds";
      this._domainEnvironment = DOMAIN_ENVIRONMENT.FUNDS;
    }
    else if (origin.includes("creative.segofinance.com") || origin.includes(stagingCreative) || origin.includes("localhost:4205"))
    {
      environment.linea = "creative";
      this._domainEnvironment = DOMAIN_ENVIRONMENT.CREATIVE;
    }
    else
    {
      environment.linea = "segofinance";
      this._domainEnvironment = DOMAIN_ENVIRONMENT.SEGOFINANCE;
    }

    registerLocaleData(es);
  }

  /* Se usa para obtener el subdominio de una plataforma */
  getSubdominioUrl(plataformaId: string)
  {
    const isProd = environment.production;
    const plataforma = environment.linea;
    const origin = isPlatformBrowser(this._platformBrowserId) ? document.location.origin : '';

    if (plataforma == plataformaId) {
      return '';
    }
    else
    {
      if (isProd)
      {
        switch (plataformaId)
        {
          case 'venture':
            return `https://venture.segofinance.com`;
          case 'realestate':
            return `https://realestate.segofinance.com`;
          case 'factoring':
            return `https://factoring.segofinance.com`;
          case 'funds':
            return `https://funds.segofinance.com`;
          case 'creative':
            return `https://creative.segofinance.com`;
          case 'panel-emprendedor':
            return 'https://financiacion.segofinance.com'
          case 'segofinance':
          default:
            return `https://www.segofinance.com`;
        }
      } else
      {
        if (origin.includes('segofinance.com'))
        {
          switch (plataformaId)
          {
            case 'venture':
              return `https://devventure.segofinance.com`;
            case 'realestate':
              return `https://devrealestate.segofinance.com`;
            case 'factoring':
              return `https://devfactoring.segofinance.com`;
            case 'funds':
              return `https://devfunds.segofinance.com`;
            case 'creative':
              return `https://devcreative.segofinance.com`;
            case 'panel-emprendedor':
              return `https://devfinanciacion.segofinance.com/`;
            case 'segofinance':
            default:
              return `https://dev.segofinance.com`;
          }
        }
        else
        {
          {
            switch (plataformaId)
            {
              case 'venture':
                return `http://localhost:4201`;
              case 'realestate':
                return `http://localhost:4202`;
              case 'factoring':
                return `http://localhost:4203`;
              case 'funds':
                return `http://localhost:4204`;
              case 'creative':
                return `http://localhost:4205`;
              case 'segofinance':
              default:
                return `http://localhost:4200`;
            }
          }
        }
      }
    }
  }

  private openWindow(url: string, target?: "_blank"): void
  {
    if (isPlatformBrowser(this._platformBrowserId))
    {
      window.open(url, target ? target : '_self');
    }
  }

  /* Se usa para mostrar popup de aviso al salir de venture o realestate (CNMV) */
  openAvisoSalidaCnmv(plataformaOrigen: string, plataformaDestino: string, url: string)
  {
    const domainUrl = this.getSubdominioUrl(plataformaDestino);
    url = domainUrl + url;
    let destacadoDestino: string;
    let informativoDestino: string;

    if (plataformaDestino == 'venture' || plataformaDestino == 'realestate' || plataformaDestino == 'creative')
    {
      destacadoDestino = "y entrando en SegoVenture que opera bajo SociosInversores 2010 PFP SL, empresa autorizada por la CNMV.";
      informativoDestino = "<strong>SegoVenture</strong> opera bajo <strong>SociosInversores 2010 SL</strong>, que es un Proveedor de Servicios de Financiación Participativa regulada por la Comisión Nacional del Mercado de Valores (CNMV) y registrada en la Autoridad Europea de Supervisión de Valores y Mercados (AEVM). SociosInversores SL es una empresa del Grupo SEGOFINANCE.";
    } else if (plataformaDestino == 'factoring')
    {
      destacadoDestino = "y entrando en SegoFactoring.";
      informativoDestino = "<strong>SegoFactoring</strong> es un producto ofrecido por <strong>Smart and Small SL</strong>, sociedad del grupo SegoFinance. No sujeto al Reglamento (UE) 2020/1503 del Parlamento Europeo y por tanto, no está supervisado por CNMV.";
    } else if (plataformaDestino == 'funds')
    {
      destacadoDestino = "y entrando en SegoFunds.";
      informativoDestino = "<strong>SegoFunds</strong> es un producto ofrecido por <strong>Fintup Digital S.L</strong>, sociedad del grupo SegoFinance, y Agente Financiero de MyInvestor Banco S.A., y Agente de Seguros de MEDVIDA Partners, con Código mediador: C0559B87860854.";
    } else
    {
      destacadoDestino = "y entrando en Sego Finance SL, matriz del Grupo SEGOFINANCE."
      informativoDestino = "";
    }

    switch (plataformaOrigen)
    {
      case "venture":
        this._dialog.open(DialogRedireccionComponent, {
          panelClass: ['bg-primary-300', 'dialog-mb-w100'],
          autoFocus: false,
          data: {
            textoDestacado: `Estás saliendo de SegoVenture, SegoRealEstate y SegoCreative, productos ofrecidos por SociosInversores 2010 SL, sociedad del grupo SegoFinance y Proveedor de Servicios de Financiación Participativa, supervisado por CNMV y registrado en la Autoridad Europea de Supervisión de Valores y Mercados (AEVM) con Licencia nº 7. Sujeto al Reglamento (UE) 2020/1503 del Parlamento Europeo; ${destacadoDestino}`,
            textoInformativo: `${informativoDestino}`
          }
        }).afterClosed().subscribe({
          next: (confirm) =>
          {
            if (confirm)
            {
              this._dialog.closeAll(),
                this.openWindow(url, '_blank');
            }
          },
          error: (e) =>
          {
            this._matSnackBar.open(`${(e as ValidationProblem).error?.detail}`, 'x', new SnackBarErrorConfig());
          }
        })
        break;
      case "realestate":
        this._dialog.open(DialogRedireccionComponent, {
          panelClass: ['bg-primary-300', 'dialog-mb-w100'],
          autoFocus: false,
          data: {
            textoDestacado: `Estás saliendo de SegoVenture, SegoRealEstate y SegoCreative, productos ofrecidos por SociosInversores 2010 SL, sociedad del grupo SegoFinance y Proveedor de Servicios de Financiación Participativa, supervisado por CNMV y registrado en la Autoridad Europea de Supervisión de Valores y Mercados (AEVM) con Licencia nº 7. Sujeto al Reglamento (UE) 2020/1503 del Parlamento Europeo; ${destacadoDestino}`,
            textoInformativo: `${informativoDestino}`
          }
        }).afterClosed().subscribe({
          next: (confirm) =>
          {
            if (confirm)
            {
              this._dialog.closeAll(),
                this.openWindow(url, '_blank');
            }
          },
          error: (e) =>
          {
            this._matSnackBar.open(`${(e as ValidationProblem).error?.detail}`, 'x', new SnackBarErrorConfig());
          }
        })
        break;
      case "creative":
        this._dialog.open(DialogRedireccionComponent, {
          panelClass: ['bg-primary-300', 'dialog-mb-w100'],
          autoFocus: false,
          data: {
            textoDestacado: `Estás saliendo de SegoVenture, SegoRealEstate y SegoCreative, productos ofrecidos por SociosInversores 2010 SL, sociedad del grupo SegoFinance y Proveedor de Servicios de Financiación Participativa, supervisado por CNMV y registrado en la Autoridad Europea de Supervisión de Valores y Mercados (AEVM) con Licencia nº 7. Sujeto al Reglamento (UE) 2020/1503 del Parlamento Europeo; ${destacadoDestino}`,
            textoInformativo: `${informativoDestino}`
          }
        }).afterClosed().subscribe({
          next: (confirm) =>
          {
            if (confirm)
            {
              this._dialog.closeAll(),
                this.openWindow(url, '_blank');
            }
          },
          error: (e) =>
          {
            this._matSnackBar.open(`${(e as ValidationProblem).error?.detail}`, 'x', new SnackBarErrorConfig());
          }
        })
        break;
      case "factoring":
        this._dialog.open(DialogRedireccionComponent, {
          panelClass: ['bg-primary-300', 'dialog-mb-w100'],
          autoFocus: false,
          data: {
            ref: plataformaOrigen,
            destino: plataformaDestino,
            textoDestacado: `Estás saliendo de SegoFactoring, es un producto ofrecido por Smart and Small SL, sociedad del grupo SegoFinance. No sujeto al Reglamento (UE) 2020/1503 del Parlamento Europeo y por tanto, no está supervisado por CNMV, ${destacadoDestino}`,
            textoInformativo: `${informativoDestino}`
          },
        }).afterClosed().subscribe({
          next: (confirm) =>
          {
            if (confirm)
            {
              this._dialog.closeAll();
              if (plataformaDestino === 'segofinance' || plataformaDestino === 'funds')
              {
                this.openWindow(url);
              } else
              {
                this.openWindow(url, '_blank');
              }
            }
          },
          error: (e) =>
          {
            this._matSnackBar.open(`${(e as ValidationProblem).error?.detail}`, 'x', new SnackBarErrorConfig());
          }
        })
        break;
      case "funds":
        this._dialog.open(DialogRedireccionComponent, {
          panelClass: ['bg-primary-300', 'dialog-mb-w100'],
          autoFocus: false,
          data: {
            textoDestacado: `Estás saliendo de SegoFunds, un producto ofrecido por Fintup Digital S.L, sociedad del Grupo SEGOFINANCE, Agente de MyInvestor Banco S.A., y Agente de Seguros de MEDVIDA Partners, con Código mediador: C0559B87860854, ${destacadoDestino}`,
            textoInformativo: `${informativoDestino}`
          }
        }).afterClosed().subscribe({
          next: (confirm) =>
          {
            if (confirm)
            {
              this._dialog.closeAll();
              if (plataformaDestino === 'segofinance' || plataformaDestino === 'factoring')
              {
                this.openWindow(url);
              } else
              {
                this.openWindow(url, '_blank');
              }
            }
          },
          error: (e) =>
          {
            this._matSnackBar.open(`${(e as ValidationProblem).error?.detail}`, 'x', new SnackBarErrorConfig());
          }
        })
        break;
      case 'segofinance':
        this.openWindow(url, '_blank');
        break;
      default:
        this._router.navigateByUrl(url);
        break;
    }
  }
}
